import { FormWrapper } from "../FormWrapper";
import { Checkbox, FormControl, FormControlLabel, FormHelperText, Stack, TextField, TextFieldProps, Typography, styled } from "@mui/material";
import FormLabel from "../FormLabel";
import { FloatingUI } from "../FloatingUI";
import { ReservationState } from "../../util/useReservationState";
import { validateMail, validateName, validatePhone } from "../../util/validater";
import Space from "../Space";
import { ColorHexCodes } from "../../util/Color";
import useEventId from "../../util/useEventId";
import { useApiContext } from "../../api/ApiProvider";
import { useState } from "react";

const TextFieldWithFieldColor = styled((props: TextFieldProps) => <TextField {...props} />)(({ theme, error }) => ({
  "& .MuiInputBase-root": {
    backgroundColor: error ? `#${ColorHexCodes.ErrorColor}` : "transparent",
  },
  "& .MuiFormHelperText-root": {
    margin: 0,
  },
  "& .MuiInputBase-input": {
    height: "1rem",
  },
}));

export default function Form2({ state }: { state: ReservationState }) {
  const { api } = useApiContext();
  const eventId = useEventId();
  const isNameValid = validateName(state.data.name);
  const isPhoneValid = validatePhone(state.data.phone);
  const isMailValid = validateMail(state.data.mail);
  const cancelPolicyEnabled = state.data.store!.cancel_policy !== null;
  const [cancelPolicyAccepted, setCancelPolicyAccepted] = useState(false);
  const isValid = isNameValid && isPhoneValid && isMailValid && (!cancelPolicyEnabled || cancelPolicyAccepted);

  const searchParams = new URLSearchParams(window.location.search);
  const canGoBack = searchParams.get("shop") === null || searchParams.get("course") === null || searchParams.get("startAt") === null;

  const onPrev = () => {
    state.setValue("date", undefined);
  };

  const onNext = () => {
    if (state.data.store?.skip_confirm) {
      if (state.data.posted) {
        return;
      }
      state.setValue("posted", true);
      api.PostReserve(state.data, eventId, searchParams).then((response) => {
        if (response) {
          state.setValue("rid", response.rid);
          state.setValue("lineLoginUrl", response.line_login_url);
          state.setValue("accepted", true);
        } else {
          state.setValue("error", true);
        }
      });
    } else {
      state.setValue("confirmed", true);
    }
  };

  return (
    <FormWrapper currentIndex={1} skipConfirm={state.data.store?.skip_confirm}>
      <Stack style={{ gap: 20 }}>
        <FormItem required error={!isNameValid} text="お名前" placeholder="山田花子" value={state.data.name} onChange={(v) => state.setValue("name", v)} />
        <FormItem required error={!isPhoneValid} text="電話番号" placeholder="08012345678" value={state.data.phone} onChange={(v) => state.setValue("phone", v)} />
        <FormItem required error={!isMailValid} text="メールアドレス" placeholder="info@example.com" value={state.data.mail} onChange={(v) => state.setValue("mail", v)} />
        <FormItem optional multiline text="ご要望" placeholder={state.data.store?.reservation_request_placeholder ?? ""} value={state.data.request} onChange={(v) => state.setValue("request", v)} />
        {cancelPolicyEnabled && <CancelPolicy cancelPolicy={state.data.store!.cancel_policy} accepted={cancelPolicyAccepted} setAccepted={setCancelPolicyAccepted} />}
      </Stack>
      <Space h={160} />
      <FloatingUI state={state} canGoBack={canGoBack} isValid={isValid} onPrev={onPrev} onNext={onNext} />
    </FormWrapper>
  );
}

function FormItem({ text, placeholder, value, onChange, multiline, required, optional, error }: { text: string; placeholder: string; value: string; onChange: (value: string) => void; multiline?: boolean; required?: boolean; optional?: boolean; error?: boolean }) {
  const minRows = multiline ? 4 : 1;
  return (
    <Stack style={{ gap: 10 }}>
      <FormLabel required={required} optional={optional} text={text} />
      <TextFieldWithFieldColor error={error} multiline={multiline} placeholder={placeholder} minRows={minRows} value={value} onChange={(e) => onChange(e.target.value)} helperText={text + "を入力してください"} />
    </Stack>
  );
}

function CancelPolicy({ cancelPolicy, accepted, setAccepted }: { cancelPolicy: string | null; accepted: boolean; setAccepted: (accepted: boolean) => void }) {
  if (cancelPolicy === null) {
    return <></>;
  }
  return (
    <Stack style={{ gap: 10 }}>
      <FormLabel required={true} text={"キャンセルポリシー"} />
      <Typography fontSize=".8rem" fontWeight="bold">
        {cancelPolicy}
      </Typography>
      <FormControl required error={!accepted} component="fieldset" sx={{ m: 0 }} variant="standard">
        <Stack direction="row" alignItems="center">
          <FormControlLabel required control={<Checkbox value={accepted} onChange={(e) => setAccepted(e.target.checked)} />} label="キャンセルポリシーに同意する" />
        </Stack>
        <FormHelperText>キャンセルポリシーを確認の上チェックをお願いします</FormHelperText>
      </FormControl>
    </Stack>
  );
}
